import { lazy, Suspense, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useDispatchApp } from "../../lib/redux";
import { startGetReportPdfSnapshot } from "../../actions/reportPdf";
import { ReportSnapshotProvider } from "../../features/reportPdf/ReportSnapshotContext";

const GlobalReport = lazy(() => import("../GlobalReport/GlobalReport"));
const AdsCampaignStatistics = lazy(
  () => import("../AdsCampaignStatistics/AdsCampaignStatistics")
);

const REPORT_TYPE_GLOBAL = "global";
const REPORT_TYPE_CAMPAIGN = "campaign";

const REPORT_PAGE_BY_TYPE = {
  [REPORT_TYPE_GLOBAL]: GlobalReport,
  [REPORT_TYPE_CAMPAIGN]: AdsCampaignStatistics,
};

const markPdfError = (message) => {
  window.__PDF_ERROR__ = message;
};

/**
 * Public page opened by the headless browser. Loads the stored snapshot and
 * renders the regular report page in snapshot mode. The pages themselves are
 * responsible for setting `window.__PDF_READY__ = true` once fully painted.
 */
const ReportPdfRender = () => {
  const dispatch = useDispatchApp();
  const { reportType } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [snapshot, setSnapshot] = useState(null);

  useEffect(() => {
    document.documentElement.classList.add("pdf-export");
    return () => document.documentElement.classList.remove("pdf-export");
  }, []);

  useEffect(() => {
    let isCancelled = false;

    dispatch(startGetReportPdfSnapshot(token)).then((response) => {
      if (isCancelled) return;
      if (!response?.ok || response.reportType !== reportType) {
        markPdfError(response?.message || "Invalid report snapshot");
        return;
      }
      setSnapshot(response.snapshot);
    });

    return () => {
      isCancelled = true;
    };
  }, [dispatch, reportType, token]);

  const ReportPage = REPORT_PAGE_BY_TYPE[reportType];
  if (!ReportPage) {
    markPdfError(`Unknown report type: ${reportType}`);
    return null;
  }

  if (!snapshot) {
    return null;
  }

  return (
    <ReportSnapshotProvider snapshot={snapshot}>
      <Suspense fallback={null}>
        <ReportPage />
      </Suspense>
    </ReportSnapshotProvider>
  );
};

export default ReportPdfRender;
